<script setup>
import { ref, defineProps, onMounted } from 'vue'
import { formatDate } from '@/mixins/global/globalPlaces'

const props = defineProps({
    placeInformation: {
        type: Object,
        default: null
    }
})

const dayDate = ref('')
const activePlaces = ref([])
const weatherData = ref(null)

onMounted(() => {
    if (props.placeInformation && Object.keys(props.placeInformation).length > 0) {
        const today = new Date().toISOString().split('T')[0]
        const availableDates = Object.keys(props.placeInformation).sort()

        if (availableDates.includes(today)) {
            dayDate.value = today
        } else {
            dayDate.value = availableDates[0]
        }

        activePlaces.value = props.placeInformation[dayDate.value]?.places ?? []
        weatherData.value = props.placeInformation[dayDate.value]?.weather ?? null
    }
})

function getAttributesWithIcons(attributes) {
    return attributes.filter(attribute => attribute.activeIcon || attribute.inactiveIcon)
}
</script>

<template>
    <div class="day-overview-simple">
        <div v-if="!placeInformation || Object.keys(placeInformation).length === 0" class="places-empty">
            Keine Platzinformationen verfügbar.
        </div>

        <template v-else>
            <div v-if="dayDate" class="day-date">
                Platzstatus am {{ formatDate(dayDate, true) }}
            </div>

            <div class="places-row">
                <div v-for="(place, index) in activePlaces" :key="index" class="item-card">
                    <div class="item-header">
                        <div class="item-name">{{ place.name }}:</div>
                        <div class="item-status-with-icons" :class="place.open ? 'open' : 'closed'">
                            <span class="status-text">{{ place.open ? 'geöffnet' : 'geschlossen' }}</span>
                            <div class="icons-container">
                                <div class="icons-wrapper">
                                    <template v-for="(group, groupIndex) in place.attributes" :key="groupIndex">
                                        <template v-for="(attribute, attributeIndex) in getAttributesWithIcons(group.attributes)" :key="attributeIndex">
                                            <div class="separator"></div>
                                            <img v-if="attribute.active && attribute.activeIcon"
                                                 :src="attribute.activeIcon"
                                                 :title="attribute.title"
                                                 :alt="attribute.title"
                                                 class="item-icon">
                                            <img v-else-if="!attribute.active && attribute.inactiveIcon"
                                                 :src="attribute.inactiveIcon"
                                                 :title="attribute.title"
                                                 :alt="attribute.title"
                                                 class="item-icon">
                                        </template>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="place.open && place.openingTime" class="opening-hours">
                        Startzeit: {{ place.openingTime }} {{ place.closingTime ? 'bis ' + place.closingTime + ' Uhr' : 'Uhr' }}
                    </div>
                    <span class="description">{{ place.description }}</span>
                </div>

                <div v-if="weatherData?.weather?.[0]" class="item-card">
                    <div class="item-header">
                        <div class="item-name">Wetter:</div>
                    </div>
                    <span class="description">
                        <img :src="'https://openweathermap.org/img/wn/' + weatherData.weather[0].icon + '.png'"
                             :alt="weatherData.weather[0].description"
                             class="weather-icon">
                        {{ weatherData.temp ? Math.floor(weatherData.temp.day) + ' °C' : 'N/A' }}
                    </span>
                </div>
            </div>
        </template>
    </div>
</template>

<style scoped lang="scss">
.day-date {
    margin-bottom: 1rem;
}

.places-row {
    display: flex;
    flex-wrap: wrap;
    row-gap: 1.5rem;
}

.item-card {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding-right: 1.5rem;
    flex-basis: 25%;
}

.item-header {
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

.item-name {
    font-weight: bold;
}

.item-name,
.status-text,
.opening-hours {
    word-break: normal;
}

.item-status-with-icons {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &.open {
        color: var(--true-color);
    }

    &.closed {
        color: var(--false-color);
    }

    .icons-container {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .separator {
        width: 0.0625rem;
        height: 1.25rem;
        background-color: #ccc;
    }

    .icons-wrapper {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .item-icon {
        width: 1.25rem;
        height: 1.25rem;
    }
}

.weather-icon {
    width: 2rem;
    height: 2rem;
    vertical-align: middle;
}

.description {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}
</style>
